import React, { useEffect, useState, useCallback } from 'react';
import { db } from '../firebase';
import TicketCard from '../components/TicketCard';

import { collection, addDoc, query, where, getDocs } from 'firebase/firestore';

// Generate a unique ticket ID
function generateTicketId() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let result = '';
  for (let i = 0; i < 4; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return `LLC-2026-${result}`;
}

// WhatsApp number — TODO: Replace with your actual WhatsApp number
const WHATSAPP_NUMBER = '237683340380';
const MOMO_NUMBER = '683668518';
const MOMO_NAME = 'Nana Lynn';

const Tickets = () => {
  const [step, setStep] = useState(1); // 1=register, 2=payment, 3=ticket
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    ticketType: '',
    linkedin: '',
  });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [ticketId, setTicketId] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkEmail, setCheckEmail] = useState('');
  const [checkResult, setCheckResult] = useState(null);
  const [checkLoading, setCheckLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (step) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const handleInputChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onload = (ev) => setPhotoPreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    const newTicketId = generateTicketId();
    setTicketId(newTicketId);

    // Try to save to Firestore
    if (db) {
      try {
        await addDoc(collection(db, 'registrations'), {
          ticketId: newTicketId,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          ticketType: formData.ticketType,
          linkedin: formData.linkedin,
          status: 'pending',
          createdAt: new Date().toISOString(),
        });
      } catch (err) {
        console.error('Firestore save failed:', err.message);
        alert('Firebase Error: Failed to save registration. ' + err.message);
      }
    } else {
      console.warn('Running in offline mode — registration saved locally');
    }

    setLoading(false);
    setStep(2);
  };

  const handleCheckTicket = useCallback(async (emailToCheck) => {
    const emailVal = emailToCheck || checkEmail;
    if (!emailVal) return;
    setCheckLoading(true);
    setCheckResult(null);

    if (db) {
      try {
        const q = query(
          collection(db, 'registrations'),
          where('email', '==', emailVal.toLowerCase().trim())
        );
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          const doc = snapshot.docs[0].data();
          setCheckResult(doc);
          if (doc.status === 'confirmed') {
            setFormData({
              name: doc.name || '',
              email: doc.email || '',
              phone: doc.phone || '',
              ticketType: doc.ticketType || 'standard',
              linkedin: doc.linkedin || '',
            });
            setTicketId(doc.ticketId);
            setStep(3);
          }
        } else {
          setCheckResult({ notFound: true });
        }
      } catch (err) {
        console.error('Firestore query failed:', err.message);
        setCheckResult({ error: true });
      }
    } else {
      setCheckResult({ notFound: true, offline: true });
    }
    setCheckLoading(false);
  }, [checkEmail]);

  const handleSelectTicket = (type) => {
    setFormData(prev => ({ ...prev, ticketType: type }));
    setTimeout(() => {
      const el = document.getElementById('register');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const getTicketDetails = () => {
    switch (formData.ticketType) {
      case 'regular_earlybird':
        return { label: 'Regular (Early Bird)', price: '10,000', codePrice: 10000 };
      case 'regular_late':
        return { label: 'Regular (Late Registration)', price: '15,000', codePrice: 15000 };
      case 'vip_earlybird':
        return { label: 'VIP (Early Bird)', price: '35,000', codePrice: 35000 };
      case 'vip_late':
        return { label: 'VIP (Late Registration)', price: '50,000', codePrice: 50000 };
      default:
        return { label: 'Ticket', price: '0', codePrice: 0 };
    }
  };
  const { label: ticketLabel, price: ticketPrice, codePrice } = getTicketDetails();

  const whatsappMessage = encodeURIComponent(
    `Hello! I've made my payment for LLC 2026.\n\n` +
    `🎫 Ticket ID: ${ticketId}\n` +
    `👤 Name: ${formData.name}\n` +
    `📧 Email: ${formData.email}\n` +
    `📱 Phone: ${formData.phone}\n` +
    `🏷️ Type: ${ticketLabel} Pass (${ticketPrice} FCFA)\n\n` +
    `Please confirm my registration. Thank you!`
  );

  return (
    <>
      <section className="page-hero">
        <div className="container page-hero__content">
          <h1 className="page-hero__title">Get Your Ticket</h1>
          <div className="breadcrumb">
            <a href="/">Home</a> <span>/</span> Tickets
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">

          {/* ===== STEP INDICATOR ===== */}
          <div className="ticket-steps">
            <div className={`ticket-step ${step >= 1 ? 'ticket-step--active' : ''} ${step > 1 ? 'ticket-step--done' : ''}`}>
              <div className="ticket-step__circle">{step > 1 ? '✓' : '1'}</div>
              <span className="ticket-step__text">Register</span>
            </div>
            <div className="ticket-step__line"></div>
            <div className={`ticket-step ${step >= 2 ? 'ticket-step--active' : ''} ${step > 2 ? 'ticket-step--done' : ''}`}>
              <div className="ticket-step__circle">{step > 2 ? '✓' : '2'}</div>
              <span className="ticket-step__text">Payment</span>
            </div>
            <div className="ticket-step__line"></div>
            <div className={`ticket-step ${step >= 3 ? 'ticket-step--active' : ''}`}>
              <div className="ticket-step__circle">3</div>
              <span className="ticket-step__text">Ticket</span>
            </div>
          </div>

          {/* ===== STEP 1: REGISTER ===== */}
          {step === 1 && (
            <div className="ticket-flow-content">
              {/* Pricing cards */}
              <div className="text-center mb-12">
                <div className="tour-numbers-eyebrow">
                  <span className="tour-numbers-eyebrow-line"></span>
                  PRICING
                </div>
                <h2 className="tour-numbers-title" style={{ fontSize: 32 }}>CHOOSE YOUR PASS</h2>
                <div className="tour-numbers-divider" style={{ maxWidth: 400 }}></div>
              </div>

              <div className="tickets-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', maxWidth: 720 }}>
                {/* Regular Ticket - Early Bird */}
                <div
                  className={`ticket-card ticket-select-card ${formData.ticketType === 'regular_earlybird' ? 'ticket-card--selected' : ''}`}
                  onClick={() => handleSelectTicket('regular_earlybird')}
                  role="button"
                  tabIndex={0}
                >
                  <div className="ticket-card__ribbon" style={{ background: 'var(--green)' }}>Ends July 31</div>
                  <div className="ticket-card__type">Early Bird</div>
                  <h3 className="ticket-card__name">Regular</h3>
                  <div className="ticket-card__price">10,000 <small>FCFA</small></div>
                  <ul className="ticket-features">
                    <li><span className="tick">✓</span> Access to all keynote sessions & panel discussions</li>
                    <li><span className="tick">✓</span> Regular Delegate flyer</li>
                    <li><span className="tick">✓</span> Certificate of Participation (Soft copy)</li>
                    <li><span className="tick">✓</span> Access to event community group</li>
                    <li><span className="tick">✓</span> Refreshment</li>
                    <li style={{ color: 'var(--green)', fontWeight: 600 }}><span className="tick">✓</span> Early Bird discount price</li>
                  </ul>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>Late Registration: <strong>15,000 FCFA</strong></p>
                  {formData.ticketType === 'regular_earlybird' && <div className="ticket-card__check" style={{ background: '#ecfdf5', color: 'var(--green)' }}>✓ Selected</div>}
                </div>

                {/* Regular Ticket - Late Registration */}
                <div
                  className={`ticket-card ticket-select-card ${formData.ticketType === 'regular_late' ? 'ticket-card--selected' : ''}`}
                  onClick={() => handleSelectTicket('regular_late')}
                  role="button"
                  tabIndex={0}
                >
                  <div className="ticket-card__type">Late Registration</div>
                  <h3 className="ticket-card__name">Regular</h3>
                  <div className="ticket-card__price">15,000 <small>FCFA</small></div>
                  <ul className="ticket-features">
                    <li><span className="tick">✓</span> Access to all keynote sessions & panel discussions</li>
                    <li><span className="tick">✓</span> Regular Delegate flyer</li>
                    <li><span className="tick">✓</span> Certificate of Participation (Soft copy)</li>
                    <li><span className="tick">✓</span> Access to event community group</li>
                    <li><span className="tick">✓</span> Refreshment</li>
                  </ul>
                  {formData.ticketType === 'regular_late' && <div className="ticket-card__check">✓ Selected</div>}
                </div>

                {/* VIP Ticket - Early Bird */}
                <div
                  className={`ticket-card ticket-card--featured ticket-select-card ${formData.ticketType === 'vip_earlybird' ? 'ticket-card--selected' : ''}`}
                  onClick={() => handleSelectTicket('vip_earlybird')}
                  role="button"
                  tabIndex={0}
                >
                  <div className="ticket-card__ribbon" style={{ background: 'var(--green)' }}>Ends July 31</div>
                  <div className="ticket-card__type">VIP — Early Bird</div>
                  <h3 className="ticket-card__name">VIP</h3>
                  <div className="ticket-card__price">35,000 <small>FCFA</small></div>
                  <ul className="ticket-features">
                    <li style={{ color: 'var(--red)', fontWeight: 700 }}><span className="tick" style={{ color: 'var(--red)' }}>✓</span> 2-Week Post Event LinkedIn Career Accelerator</li>
                    <li><span className="tick">✓</span> VIP Fast Track Entry</li>
                    <li><span className="tick">✓</span> Reserved VIP priority seating row</li>
                    <li><span className="tick">✓</span> Complimentary refreshments in VIP area</li>
                    <li><span className="tick">✓</span> VIP Delegate flyer</li>
                    <li><span className="tick">✓</span> Access to event community group</li>
                    <li><span className="tick">✓</span> Certificate of Participation (Hard copy)</li>
                  </ul>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>Late Registration: <strong>50,000 FCFA</strong></p>
                  {formData.ticketType === 'vip_earlybird' && <div className="ticket-card__check" style={{ background: '#ecfdf5', color: 'var(--green)' }}>✓ Selected</div>}
                </div>

                {/* VIP Ticket - Late Registration */}
                <div
                  className={`ticket-card ticket-card--featured ticket-select-card ${formData.ticketType === 'vip_late' ? 'ticket-card--selected' : ''}`}
                  onClick={() => handleSelectTicket('vip_late')}
                  role="button"
                  tabIndex={0}
                >
                  <div className="ticket-card__ribbon">Career Accelerator</div>
                  <div className="ticket-card__type">VIP — Late Registration</div>
                  <h3 className="ticket-card__name">VIP</h3>
                  <div className="ticket-card__price">50,000 <small>FCFA</small></div>
                  <ul className="ticket-features">
                    <li style={{ color: 'var(--red)', fontWeight: 700 }}><span className="tick" style={{ color: 'var(--red)' }}>✓</span> 2-Week Post Event LinkedIn Career Accelerator</li>
                    <li><span className="tick">✓</span> VIP Fast Track Entry</li>
                    <li><span className="tick">✓</span> Reserved VIP priority seating row</li>
                    <li><span className="tick">✓</span> Complimentary refreshments in VIP area</li>
                    <li><span className="tick">✓</span> VIP Delegate flyer</li>
                    <li><span className="tick">✓</span> Access to event community group</li>
                    <li><span className="tick">✓</span> Certificate of Participation (Hard copy)</li>
                  </ul>
                  {formData.ticketType === 'vip_late' && <div className="ticket-card__check">✓ Selected</div>}
                </div>
              </div>

              {/* Registration Form */}
              <div className="reg-form mt-12" id="register" style={{ maxWidth: 800, margin: '64px auto 0' }}>
                <h3 className="section-title" style={{ fontSize: 28, marginBottom: 32 }}>Registration Form</h3>
                <form onSubmit={handleRegister}>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Full Name *</label>
                      <input
                        type="text" name="name" className="form-input" required
                        value={formData.name} onChange={handleInputChange}
                        placeholder="e.g. John Doe"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email Address *</label>
                      <input
                        type="email" name="email" className="form-input" required
                        value={formData.email} onChange={handleInputChange}
                        placeholder="e.g. john@example.com"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Phone Number *</label>
                      <input
                        type="tel" name="phone" className="form-input" required
                        value={formData.phone} onChange={handleInputChange}
                        placeholder="e.g. +237 6XX XXX XXX"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Ticket Type *</label>
                      <select
                        name="ticketType" className="form-input form-select" required
                        value={formData.ticketType} onChange={handleInputChange}
                      >
                        <option value="">Select a ticket</option>
                        <option value="regular_earlybird">Regular — Early Bird (10,000 FCFA)</option>
                        <option value="regular_late">Regular — Late Registration (15,000 FCFA)</option>
                        <option value="vip_earlybird">VIP — Early Bird (35,000 FCFA)</option>
                        <option value="vip_late">VIP — Late Registration (50,000 FCFA)</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">LinkedIn Profile URL (Optional)</label>
                      <input
                        type="url" name="linkedin" className="form-input"
                        value={formData.linkedin} onChange={handleInputChange}
                        placeholder="https://linkedin.com/in/yourprofile"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Your Photo (Optional — for your ticket)</label>
                      <div className="photo-upload-area">
                        <input
                          type="file" accept="image/*" id="photo-upload"
                          className="photo-upload-input" onChange={handlePhotoChange}
                        />
                        <label htmlFor="photo-upload" className="photo-upload-label">
                          {photoPreview ? (
                            <img src={photoPreview} alt="Preview" className="photo-upload-preview" />
                          ) : (
                            <div className="photo-upload-placeholder">
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                <polyline points="21 15 16 10 5 21"></polyline>
                              </svg>
                              <span>Click to upload photo</span>
                            </div>
                          )}
                        </label>
                      </div>
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="form-submit mt-4"
                    disabled={loading || !formData.ticketType}
                  >
                    {loading ? 'Processing...' : 'Proceed to Payment →'}
                  </button>
                </form>
              </div>

              {/* Check existing ticket */}
              <div className="ticket-check-section">
                <div className="ticket-check-card">
                  <h4 className="ticket-check-title">Already registered?</h4>
                  <p className="ticket-check-text">Enter your email to check your ticket status or download your ticket.</p>
                  <div className="ticket-check-form">
                    <input
                      type="email"
                      className="form-input"
                      placeholder="Enter your email address"
                      value={checkEmail}
                      onChange={(e) => setCheckEmail(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleCheckTicket()}
                    />
                    <button
                      className="btn btn-blue"
                      onClick={() => handleCheckTicket()}
                      disabled={checkLoading}
                    >
                      {checkLoading ? 'Checking...' : 'Check Status'}
                    </button>
                  </div>
                  {checkResult && checkResult.notFound && (
                    <div className="ticket-check-result ticket-check-result--error">
                      {checkResult.offline
                        ? '⚠️ Firebase is not configured yet. Please set up Firebase to enable ticket lookup.'
                        : '❌ No registration found for this email. Please register first.'}
                    </div>
                  )}
                  {checkResult && checkResult.error && (
                    <div className="ticket-check-result ticket-check-result--error">
                      ⚠️ Something went wrong. Please try again.
                    </div>
                  )}
                  {checkResult && checkResult.status === 'pending' && (
                    <div className="ticket-check-result ticket-check-result--pending">
                      ⏳ Your registration is pending payment confirmation. Ticket ID: <strong>{checkResult.ticketId}</strong>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ===== STEP 2: PAYMENT ===== */}
          {step === 2 && (
            <div className="ticket-flow-content" style={{ maxWidth: 700, margin: '0 auto' }}>
              <div className="payment-card">
                <div className="payment-card__header">
                  <div className="payment-card__icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                      <line x1="1" y1="10" x2="23" y2="10"></line>
                    </svg>
                  </div>
                  <h2 className="payment-card__title">Complete Your Payment</h2>
                  <p className="payment-card__subtitle">Your registration has been received! Complete payment to get your ticket.</p>
                </div>

                <div className="payment-card__details">
                  <div className="payment-detail-row">
                    <span className="payment-detail-label">Ticket ID</span>
                    <span className="payment-detail-value payment-detail-value--highlight">{ticketId}</span>
                  </div>
                  <div className="payment-detail-row">
                    <span className="payment-detail-label">Name</span>
                    <span className="payment-detail-value">{formData.name}</span>
                  </div>
                  <div className="payment-detail-row">
                    <span className="payment-detail-label">Type</span>
                    <span className="payment-detail-value">{ticketLabel} Pass</span>
                  </div>
                  <div className="payment-detail-row payment-detail-row--total">
                    <span className="payment-detail-label">Amount to Pay</span>
                    <span className="payment-detail-value payment-detail-value--amount">{ticketPrice} FCFA</span>
                  </div>
                </div>

                <div className="payment-instructions">
                  <h4 className="payment-instructions__title">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 8 }}>
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="16" x2="12" y2="12"></line>
                      <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                    How to Pay
                  </h4>
                  <ol className="payment-steps-list">
                    <li>
                      <strong>Send {ticketPrice} FCFA</strong> via MTN Mobile Money or Orange Money to:
                      <div className="momo-number" style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, margin: '8px 0', background: 'var(--blue-light)', border: '1.5px solid var(--blue)', borderRadius: '8px', padding: '12px 18px' }}>
                        <span style={{ fontSize: '20px', fontWeight: 800, color: 'var(--blue)', fontFamily: 'Montserrat, sans-serif' }}>683 668 518</span>
                        <span style={{ fontSize: '13px', color: 'var(--text-body)', fontWeight: 600 }}>Account Name: <strong style={{ color: 'var(--text-dark)' }}>{MOMO_NAME}</strong></span>
                      </div>
                    </li>
                    <li style={{ marginTop: 8 }}>
                      <strong>Launch USSD Transfer (Mobile Only)</strong>:
                      <p style={{ fontSize: '13px', color: 'var(--text-body)', marginBottom: 8 }}>
                        Click below to automatically pre-dial the money transfer code on your phone:
                      </p>
                      <div style={{ display: 'flex', gap: 12, margin: '10px 0', flexWrap: 'wrap' }}>
                        <a
                          href={`tel:*126*1*1*${MOMO_NUMBER}*${codePrice}%23`}
                          className="btn btn-sm"
                          style={{ background: '#FFCC00', color: '#000', borderRadius: '8px', padding: '10px 16px', fontWeight: '800', border: '1px solid #E6B800' }}
                        >
                          Dial MTN MoMo (*126*1*1...)
                        </a>
                        <a
                          href={`tel:%23150*1*1*655876997*${codePrice}%23`}
                          className="btn btn-sm"
                          style={{ background: '#FF6600', color: '#fff', borderRadius: '8px', padding: '10px 16px', fontWeight: '800', border: '1px solid #E65C00' }}
                        >
                          Dial Orange Money (#150*1*1...)
                        </a>
                      </div>
                      <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px', lineHeight: 1.5 }}>
                        *Note: If clicking does not launch the dialer, dial manually:<br />
                        <strong>MTN MoMo code</strong>: <code style={{ background: 'var(--gray-bg)', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>*126*1*1*{MOMO_NUMBER}*{codePrice}#</code><br />
                        <strong>Orange Money code</strong>: <code style={{ background: 'var(--gray-bg)', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>#150*1*1*{MOMO_NUMBER}*{codePrice}#</code>
                      </p>
                    </li>
                    <li style={{ marginTop: 12 }}>Take a <strong>screenshot</strong> of your payment confirmation</li>
                    <li>Click the WhatsApp button below and <strong>send the screenshot</strong> along with the pre-filled message</li>
                    <li>You'll receive a <strong>confirmation</strong> and your ticket will be ready for download</li>
                  </ol>
                </div>

                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whatsapp-btn"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 10 }}>
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  Confirm Payment on WhatsApp
                </a>

                <div className="payment-card__footer">
                  <button
                    className="payment-check-link"
                    onClick={() => {
                      setCheckEmail(formData.email);
                      handleCheckTicket(formData.email);
                    }}
                  >
                    I've already paid — check my ticket status →
                  </button>

                  {/* Demo button for testing without Firebase */}
                  <button
                    className="payment-demo-btn"
                    onClick={() => setStep(3)}
                  >
                    Preview ticket design (demo)
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ===== STEP 3: TICKET ===== */}
          {step === 3 && (
            <div className="ticket-flow-content">
              <div className="text-center mb-12">
                <div className="ticket-confirmed-badge">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                  Payment Confirmed
                </div>
                <h2 className="tour-numbers-title" style={{ fontSize: 28, marginTop: 16 }}>YOUR TICKET IS READY!</h2>
                <p style={{ color: '#6b7280', fontSize: 15, maxWidth: 500, margin: '12px auto 0' }}>
                  Download your ticket as PNG or PDF. You'll need this ticket for entry on the event day.
                </p>
              </div>

              <TicketCard
                ticketData={{
                  name: formData.name,
                  email: formData.email,
                  phone: formData.phone,
                  ticketType: formData.ticketType || 'standard',
                  ticketId: ticketId,
                  photo: photoPreview,
                }}
              />

              <div className="text-center mt-12">
                <button className="btn btn-outline" onClick={() => { setStep(1); setFormData({ name: '', email: '', phone: '', ticketType: '', linkedin: '' }); setPhoto(null); setPhotoPreview(null); }}>
                  Register Another Person
                </button>
              </div>
            </div>
          )}

        </div>
      </section>
    </>
  );
};

export default Tickets;
